import React, { FunctionComponent } from 'react';
import { Card, CardBody, CardHeader, Table, Button, DropdownItem } from 'reactstrap';
import styled from 'styled-components';
import { withTranslation, WithTranslation } from 'react-i18next';
import Helmet from 'react-helmet';

// constants
import { AuthenticationToken, ReportType } from 'constants/api';

// components
import ReportEditModal from 'components/modals/ReportEditModal';
import ReportDeleteModal from 'components/modals/ReportDeleteModal';
import ContextMenuDropdown from 'components/utils/ContextMenuDropdown';
import { compose } from 'recompose';
import { API_URL } from 'constants/endpointConfig';
import { ReduxState } from 'createStore';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

interface InProps {
    report: ReportType;
    isUserAdmin: boolean;
    pageNumber: number;
    perPage: number;
    sortBy: string;
    sortOrder: string;
}

interface EnhanceProps {
    authenticationToken: AuthenticationToken;
}

type Props = InProps & EnhanceProps & WithTranslation;

const mapStateToProps = (state: ReduxState) => ({
    authenticationToken: state.app.authenticationToken
});

const enhance = compose<Props, InProps>(
    connect(mapStateToProps),
    withTranslation()
);

const ReportInfo: FunctionComponent<Props> = ({ report, isUserAdmin, t, authenticationToken, pageNumber, perPage, sortBy, sortOrder }) => {
    const roles = report.roles
        .split(',')
        .map((role: string) => t(`rolesMap.${role}`))
        .join(', ');

    const downloadReport = () => {
        fetch(`${API_URL}/reports/${report.uuid}/download`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${authenticationToken.access_token}`
            }
        }).then(async (response) => {
            if (!response.ok) {
                const json = await response.json();
                toast.error(json.message);
                return;
            }

            const contentDisposition = response.headers.get("Content-Disposition");
            let filename = "downloaded_file";

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match) {
                    filename = match[1];
                }
            }

            const blob = await response.blob();
            const fileUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = fileUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(fileUrl);
        });
    };

    const capitalizeString = (str: string) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    return (
        <>
            <Helmet title={report.name} />
            <Card>
                <CardHeader>
                    <h5>
                        <>{report.name}</>
                        {isUserAdmin && (
                            <>
                                <ContextMenuDropdown
                                    data-test="context-menu"
                                    className="cursor-pointer fa-pull-right text-primary card-header-custom"
                                    dropdownMenuProps={{ right: true, persist: true }}
                                >
                                    <ReportEditModal
                                        report={report}
                                        pageNumber={pageNumber}
                                        perPage={perPage}
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        renderButton={(props) => (
                                            <DropdownItem toggle={false} onClick={props.onClick}>
                                                {t('reportsCommon.editReport')}
                                            </DropdownItem>
                                        )}
                                    />
                                    <ReportDeleteModal
                                        report={report}
                                        pageNumber={pageNumber}
                                        perPage={perPage}
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        renderButton={(props) => (
                                            <DropdownItem toggle={false} onClick={props.onClick}>
                                                {t('reportsCommon.deleteReport')}
                                            </DropdownItem>
                                        )}
                                    />
                                </ContextMenuDropdown>
                                <hr />
                                <div className="card-header-custom">
                                    <ReportInfoTable size="sm" className="mb-0" borderless>
                                        <tbody>
                                            <tr>
                                                <th>{t('reportsCommon.roles')}&nbsp;</th>
                                                <td className="text-bold">{roles}</td>
                                            </tr>
                                            <tr>
                                                <th>{t('reportsCommon.reportPath')}&nbsp;</th>
                                                <td>
                                                    <textarea
                                                        className="no-border"
                                                        readOnly={true}
                                                        rows={2}
                                                        cols={55}
                                                        value={report.cloud_drive_path}
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </ReportInfoTable>
                                </div>
                            </>
                        )}
                    </h5>
                </CardHeader>
                <CardBody>
                    <ReportInfoTable size="sm" className="mb-0" borderless>
                        <tbody>
                            <tr>
                                <th>{t('reportsCommon.frequency')}</th>
                                <td>{capitalizeString(report.frequency)}</td>
                            </tr>
                            <tr>
                                <th>{t('reportsCommon.description')}</th>
                                <td>
                                    <textarea
                                        className="no-border"
                                        readOnly={true}
                                        rows={2}
                                        cols={55}
                                        value={report.description}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <th />
                                <td className="allign-button-right">
                                    <Button color="primary" onClick={() => downloadReport()}>
                                        {t('reportsCommon.download')}
                                    </Button>
                                </td>
                            </tr>
                        </tbody>
                    </ReportInfoTable>
                </CardBody>
            </Card>
        </>
    );
};

const ReportInfoTable = styled(Table)`
    th {
        width: 10em;
    }
`;

export default enhance(ReportInfo);
